"use client";

import { useCallback, useState } from "react";
import { extractErrorMessages } from "@/app/components/common-form-builder/form-builder-helpers/form-functions";
import { useTranslation } from "@/hooks/localization/useTranslations";
import {
  reportUserFacingFailure,
  classifyFailure,
} from "@/utils/logging/clientErrorLogger";
import {
  FailureKind,
  type ErrorReportMeta,
} from "@/utils/logging/errorTypes";

/**
 * Error-popup state for a React Query `onError`, with Graylog reporting.
 *
 * `report` shows the backend's messages when there are any; otherwise it shows
 * a translated fallback plus a reference code and logs the error, classified as
 * api_http / api_network / frontend_exception.
 *
 * @param context the failing action, e.g. "add_to_cart" — `_context` in Graylog.
 */
export const useApiErrorPopup = (context: string) => {
  const t = useTranslation();
  const [notFieldErr, setNotFieldErr] = useState<string[]>([]);
  const [errorRef, setErrorRef] = useState<string>();

  const clearErrors = useCallback((errors: string[] = []) => {
    setNotFieldErr(errors);
    if (!errors.length) setErrorRef(undefined);
  }, []);

  const report = useCallback(
    (error: unknown, extra?: ErrorReportMeta["extra"]) => {
      const messages = extractErrorMessages(error);

      if (messages.length) {
        // The backend said what went wrong; show it as-is and do not log it.
        setErrorRef(undefined);
        setNotFieldErr(messages);
        return;
      }

      const ref = reportUserFacingFailure({ error, context, extra });
      setErrorRef(ref);
      setNotFieldErr([
        classifyFailure(error) === FailureKind.API_NETWORK
          ? t.check_internet_connection
          : t.an_error_occurred_during_the_operation,
      ]);
    },
    [context, t],
  );

  return { notFieldErr, setNotFieldErr, errorRef, report, clearErrors };
};
